import { Beech } from './beech';
import Vue from 'vue';

interface AppConfig {
	app: string;
	release: string;
}

export default class Embedded {
	private readonly beech: Beech;
	private readonly enabled: boolean = false;

	private parentAppConfig: AppConfig | null = null;

	public constructor(beech: Beech) {
		this.beech = beech;

		if (!window || !window.parent || window == window.parent) {
			logg.log('embedded >> init >> no parent');
			return;
		}

		logg.log('embedded >> init');
		this.enabled = true;

		window.addEventListener('message', this.onWindowMessage);

		const vm = new Vue({ data: beech.model.getData() });
		vm.$watch('agentState', (newValue) => this.sync({ agentState: newValue }));
		vm.$watch('username', (newValue) => this.sync({ username: newValue }));
		vm.$watch('serverState', (newValue) => this.sync({ serverState: newValue }));
		vm.$watch('availableAgents', (newValue) => this.sync({ availableAgents: newValue }));
		vm.$watch('idleAgents', (newValue) => this.sync({ idleAgents: newValue }));
		vm.$watch('busyAgents', (newValue) => this.sync({ busyAgents: newValue }));
		vm.$watch('callBranding', (newValue) => this.sync({ callBranding: newValue }));
		vm.$watch('callLocalNumber', (newValue) => this.sync({ callLocalNumber: newValue }));
		vm.$watch('callRemoteNumber', (newValue) => this.sync({ callRemoteNumber: newValue }));
		vm.$watch('callRemoteLocation', (newValue) => this.sync({ callRemoteLocation: newValue }));
		vm.$watch('callRemoteUsers', (newValue) => this.sync({ callRemoteUsers: newValue }));
		vm.$watch('callInviteFrom', (newValue) => this.sync({ callInviteFrom: newValue }));
		vm.$watch('callRecorded', (newValue) => this.sync({ callRecorded: newValue }));
		vm.$watch('lobby', (newValue) => this.sync({ lobby: newValue }));
	}

	public isEnabled = (): boolean => {
		return this.enabled;
	};

	public getParentApp = (): AppConfig | null => {
		return this.parentAppConfig;
	};

	public doReload() {
		this.postMessage('beech:doReload', {});
	}

	public onCallIncoming() {
		this.postMessage('beech:onCallIncoming', {});
	}

	public onCallConnected(residentId?: number) {
		this.postMessage('beech:onCallConnected', { residentId });
	}

	public onCallAccept(residentId?: number) {
		this.postMessage('beech:onCallAccept', { residentId });
	}

	public onCallReject() {
		this.postMessage('beech:onCallReject', {});
	}

	public onCallDisconnect() {
		this.postMessage('beech:onCallDisconnect', {});
	}

	private onWindowMessage = (event: MessageEvent) => {
		logg.log('embedded >> onWindowMessage', event);
		if (event && event.data && event.data.type && typeof event.data.type === 'string') {
			const type = event.data.type as string;
			const prefix = 'beech:';
			if (type.startsWith(prefix)) {
				this.onBeechMessage(type.slice(prefix.length), event.data.data);
			}
		}
	};

	private onBeechMessage = (type: string, data: any) => {
		logg.log('embedded >> onBeechMessage', type, data);
		switch (type) {
			case 'ready':
				this.parentAppConfig = {
					app: data.app ?? 'unknown',
					release: data.release ?? 'unknown'
				};
				this.beech.model.setEmbeddedReady(true);
				this.syncAll();
				this.beech.server.onClientInfoChanged();
				break;

			case 'login':
				this.beech.flow.doLoginSession(data.email, data.session);
				break;

			case 'toggleAvailability':
				this.beech.model.toggleAvailability();
				break;

			case 'updateHeadsetState':
				this.beech.model.setHeadsetState(data.headsetState);
				break;

			case 'dial':
				this.beech.flow.doOutgoingDial(data.number);
				break;

			case 'doCallAccept':
				this.beech.flow.doRingingAccept();
				break;

			case 'doCallReject':
				this.beech.flow.doRingingReject();
				break;

			default:
				logg.warn(`embedded >> unknown message type \`${type}\``);
				break;
		}
	};

	private syncAll = () => {
		const data = this.beech.model.getData();
		this.sync({
			listenHost: this.beech.config.listen_host,
			agentState: data.agentState,
			username: data.username,
			availableAgents: data.availableAgents,
			idleAgents: data.idleAgents,
			busyAgents: data.busyAgents,
			serverState: data.serverState
		});
	};

	private sync = (data: any) => {
		this.postMessage('beech:sync', data);
	};

	private postMessage = (type: string, data: any) => {
		logg.debug('embedded >> postMessage', { type: type, data: data });
		window.parent.postMessage({ type: type, data: data }, '*');
	};
}
